/**
 * PageHeader — kabinet-stijl uniform header voor elke module-pagina.
 *
 * Usage:
 *   <PageHeader
 *     icon={Radio}
 *     eyebrow="Show 04 · Winter 26"
 *     title="Publiek reveal — fase 2 loopt."
 *     subtitle="Jury heeft gestemd. Publiek onthult nu één voor één, laagste eerst."
 *     counterPill={{ label: 'live', tone: 'live' }}
 *     actions={<><Button>...</Button></>}
 *   />
 *
 * De oude `accent` prop wordt nog geaccepteerd (backwards compat) maar
 * genegeerd — in het kabinet-design gebruikt elke pagina hetzelfde
 * neutrale icon-tile met acid-accent op interaction/state.
 */

import { cn } from '../lib/utils'
import usePageTitle from '../lib/usePageTitle'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

// legacy `accent` names → tone map (voor evt. counter-pill fallback)
const ACCENT_TO_TONE = {
  magenta: 'peach', cyan: 'live', gold: 'gold', emerald: 'live',
  blue: 'live', orange: 'peach', purple: 'peach', rose: 'peach',
  slate: 'muted',
}

const PILL_TONES = {
  live:   'border-acid/40 text-acid bg-acid/8',
  peach:  'border-peach/40 text-peach bg-peach/8',
  gold:   'border-gold/40 text-gold bg-gold/8',
  signal: 'border-signal/40 text-signal bg-signal/8',
  muted:  'border-border text-muted-foreground',
}

export default function PageHeader({
  icon: Icon,
  title,
  subtitle,
  eyebrow,
  actions,
  counterPill,           // { label, tone: 'live'|'peach'|'gold'|'signal'|'muted' }
  accent,                // legacy, genegeerd behalve als counterPill niet gezet is
  className,
  tabTitle,              // override voor de browser-tab-titel (default: title)
  hideBack = false,      // zet true om back-knop te verbergen (bv. op inbedding)
}) {
  usePageTitle(tabTitle || (typeof title === 'string' ? title : null))
  const location = useLocation()
  const navigate = useNavigate()
  // Back-knop: browser history first. Op home ('/') geen zin — daar verbergen.
  // Als er geen history is (direct-load in nieuwe tab), val terug op home.
  const isHome = location.pathname === '/' || location.pathname === ''
  const showBack = !hideBack && !isHome
  const goBack = () => {
    if (window.history.length > 1) navigate(-1)
    else navigate('/')
  }
  // Legacy fallback: als accent gezet is en er is geen expliciete pill, geef geen pill.
  // (Bewust: we willen niet dat elke module weer een eigen accent-badge krijgt.)
  return (
    <header className={cn('pb-5 mb-6 border-b border-border', className)}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          {showBack && (
            <button
              type="button" onClick={goBack}
              className="w-10 h-10 rounded-[4px] border border-border bg-card flex items-center justify-center shrink-0 text-muted-foreground hover:text-acid hover:border-acid/40 transition-colors"
              title="Terug (browser-history)"
              aria-label="Terug">
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          {Icon && (
            <div className="w-10 h-10 rounded-[4px] border border-border bg-card flex items-center justify-center shrink-0">
              <Icon className="h-5 w-5 text-acid" />
            </div>
          )}
          <div className="min-w-0">
            {eyebrow && (
              <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-1 leading-tight">
                {eyebrow}
              </div>
            )}
            <div className="flex items-baseline gap-3 flex-wrap">
              <h1 className="text-2xl md:text-[1.65rem] font-semibold tracking-tight leading-tight m-0">
                {title}
              </h1>
              {counterPill && (
                <span className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-mono text-[11px] tracking-wide',
                  PILL_TONES[counterPill.tone] || PILL_TONES.muted,
                )}>
                  {counterPill.tone === 'live' && <span className="w-1.5 h-1.5 rounded-full bg-acid" />}
                  {counterPill.label}
                </span>
              )}
            </div>
            {subtitle && (
              <p className="mt-1.5 text-sm md:text-[0.95rem] text-muted-foreground max-w-[60ch] leading-relaxed">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {actions && (
          <div className="flex items-center gap-2 shrink-0">
            {actions}
          </div>
        )}
      </div>
    </header>
  )
}
